import React from 'react'
import axiosInstance from './axiosInstance'
import toast from 'react-hot-toast'
import { SignalSlashIcon } from '@heroicons/react/24/outline'

/**
 * Servicio para la sincronización con OFIMA
 */
class OfimaSyncService {
  async sendToOfima(orderIds: Array<string | number> | string): Promise<{
    message: string
    results: {
      success: Array<{ orderId: string; ofimaResponse?: unknown; message?: string }>
      failed: Array<{ orderId: string; error?: string; message?: string }>
    }
  }> {
    try {
      const response = await axiosInstance.post('/ofima/send-to-ofima', { orderIds })
      return response.data
    } catch (error: any) {
      console.error('Error enviando pedidos a OFIMA:', error)
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Error enviando pedidos a OFIMA'
      const statusCode = error.response?.status || 500
      throw new Error(`${errorMessage} (${statusCode})`)
    }
  }

  /**
   * Ejecuta la sincronización forzada de todas las remisiones con OFIMA
   * @returns Promise con la respuesta del servidor
   */
  async forceSyncAll(): Promise<any> {
    try {
      const response = await axiosInstance.post('/ofima-sync/force-sync-all', {})
      return response.data
    } catch (error: any) {
      console.error('Error en sincronizacion de remisiones:', error)
      const errorMessage = error.response?.data?.message || 'Error en sincronizacion de remisiones'
      const statusCode = error.response?.status || 500
      throw new Error(`${errorMessage} (${statusCode})`)
    }
  }

  /**
   * Ejecuta el proceso SPX de OFIMA
   * @returns Promise con la respuesta del servidor
   */
  async ejecutarSPX(): Promise<any> {
    try {
      const response = await axiosInstance.post('/ofima-sync/ejecutar-spx', {})
      return response.data
    } catch (error: any) {
      console.error('Error ejecutando SPX:', error)
      const errorMessage = error.response?.data?.message || 'Error ejecutando SPX'
      const statusCode = error.response?.status || 500
      throw new Error(`${errorMessage} (${statusCode})`)
    }
  }

  /**
   * Ejecuta validación local y consulta remisiones faltantes en OFIMA
   * POST /ofima-sync/ejecutar-y-consultar
   */
  async ejecutarYConsultar(pedidos: Array<string | number>): Promise<{
    success: boolean
    message: string
    spx?: { success: boolean; message: string }
    pedidos: Array<{
      Numero: string
      NumeroOfima?: string
      Procesado?: boolean | number | string
      Mensaje?: string
    }>
    totales: { total: number; procesados: number; noProcesados: number }
    timestamp: string
  }> {
    if (!Array.isArray(pedidos) || pedidos.length === 0) {
      return Promise.resolve({
        success: true,
        message: 'Sin pedidos para consultar',
        pedidos: [],
        totales: { total: 0, procesados: 0, noProcesados: 0 },
        timestamp: new Date().toISOString()
      } as any)
    }

    try {
      const body = { pedidos: pedidos.map(p => p.toString()) }
      const response = await axiosInstance.post('/ofima-sync/ejecutar-y-consultar', body)
      return response.data
    } catch (error: any) {
      console.error('Error en ejecutarYConsultar:', error)
      
      // Crear el icono una sola vez
      const errorIcon = React.createElement(SignalSlashIcon, { className: 'w-5 h-5 text-red-500' })
      
      // Si es un error 500, mostrar toast específico
      if (error.response?.status === 500) {
        toast.error('No se pudo conectar con OFIMA. Por favor, intenta más tarde.', {
          icon: errorIcon,
          duration: 5000,
          position: 'top-center',
        })
      } else if (error.response?.status) {
        // Otros errores HTTP
        const errorMessage = error.response?.data?.message || `Error ${error.response.status} al consultar OFIMA`
        toast.error(errorMessage, {
          icon: errorIcon,
          duration: 4000,
          position: 'top-center',
        })
      } else {
        // Error de red o timeout
        toast.error('Error de conexión al consultar OFIMA..', {
          icon: errorIcon,
          duration: 4000,
          position: 'top-center',
        })
      }
      
      // Re-lanzar el error para que el código que llama pueda manejarlo si es necesario
      throw error
    }
  }
}

const ofimaSyncService = new OfimaSyncService()
export default ofimaSyncService

